import type { ComponentProps } from "react";
import { useId } from "react";

import { CATEGORY_COLOR } from "@/features/products/models";
import { Badge, RadioGroup } from "@/shared/ui";
import { tw } from "@/shared/utils";
import type { AvailableRefillRequest } from "../models";
import { RefillDisabledAlert } from "./refill-disabled-alert";

interface RefillOptionsRadioGroupProps
  extends ComponentProps<typeof RadioGroup.Root> {
  refillRequests: AvailableRefillRequest[];
}

export const RefillOptionsRadioGroup = ({
  refillRequests,
  ...props
}: RefillOptionsRadioGroupProps) => {
  const id = useId();

  return (
    <RadioGroup.Root isCard color="brown" className="grow" {...props}>
      {refillRequests.map(
        ({
          dosis,
          frequency,
          product,
          isRefillable,
          hasUpcomingAppointment,
          isSyncRequired,
          hasPendingActions,
        }) => {
          const disabled =
            !isRefillable ||
            hasUpcomingAppointment ||
            (isSyncRequired && !hasPendingActions);

          return (
            <RadioGroup.Item
              key={`${id}-${product.id}`}
              value={product.slug}
              disabled={disabled}
            >
              <div className={tw("flex flex-col", disabled && "opacity-50")}>
                <RadioGroup.Label className="inline-flex justify-between gap-2">
                  {product.name}

                  <Badge
                    color={CATEGORY_COLOR[product.category.slug]}
                    size="small"
                  >
                    {product.category.name}
                  </Badge>
                </RadioGroup.Label>

                <RadioGroup.Description>
                  {dosis} {frequency}
                </RadioGroup.Description>
              </div>

              {disabled && (
                <RefillDisabledAlert
                  isRefillable={isRefillable}
                  hasUpcomingAppointment={hasUpcomingAppointment}
                  isSyncRequired={isSyncRequired}
                />
              )}
            </RadioGroup.Item>
          );
        },
      )}
    </RadioGroup.Root>
  );
};
RefillOptionsRadioGroup.displayName = "RefillOptionsRadioGroup";
